<template>
  <div>
    <b-modal
      v-model="showUpdateUser"
      type="warn"
      title="Confirm Update"
      @ok="onSaveUser"
      @cancel="resetData"
      ok-title="Yes, Save"
    >
      <p>Changes made to the coordinator will be reflected in all media calls it is being used at. Are you sure you want to save the coordinator?</p>
    </b-modal>
    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(showConfirmModal)">
        <div class="row">
          <div v-show="hasError" class="col-md-12 mb-3">
            <div class="alert alert-danger alert-dismissible fade show" role="alert">
              {{ errorMessage }}
              <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>First Name*</label>
              <ValidationProvider mode="passive" rules="required" v-slot="{ errors }">
                <input type="text" v-model="userData.first_name" class="form-control" ref="focusThis" />
                <span class="text-danger" v-for="(error, index) in errors" :key="index">{{ error }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Last Name</label>
              <input type="text" v-model="userData.last_name" class="form-control" />
            </div>
          </div>
          <div class="col-md-12">
            <div class="form-group">
              <label>Email*</label>
              <ValidationProvider mode="passive" rules="required|email" v-slot="{ errors }">
                <input type="email" v-model="userData.email" class="form-control" :readonly="userData.id != '' && userData.email != ''" />
                <span class="text-danger" v-for="(error, index) in errors" :key="index">{{ error }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="col-md-12">
            <div class="form-group">
              <a href="#" class="" :class="psExpanded ? 'bordered-link': ''" @click.prevent="toggleRows">
                <span> Set New Password
                  <i v-if="psExpanded" class="fas fa-chevron-up"></i>
                  <i v-else class="fas fa-chevron-down"></i>
                </span>
              </a>
            </div>
          </div>
          <transition name="fade">
            <div class="row m-0" v-if="psExpanded">
              <div class="col-md-12 ">
                <div class="row bordered-parent">
                  <!-- enter current password if editing password for current user -->
                  <div class="col-md-12 pr-0 pl-0" v-if="psExpanded && loggedInUserCompare">
                    <div class="form-group">
                      <label>Current Password*</label>
                      <ValidationProvider mode="passive" :rules="password != '' ? 'required' : ''"  v-slot="{ errors }">
                        <input :type="showCurrentPassword ? 'text' : 'password'" 
                          v-model="currentPassword" class="form-control" />
                        <span class="toggle-password top-55" @click="toggleCurrentPassword">
                          <i v-if="showCurrentPassword" class="fas fa-eye"></i>
                          <i v-else class="fas fa-eye-slash"></i>
                        </span>
                        <span class="text-danger" v-for="(error, index) in errors" :key="index">{{ error }}</span>
                      </ValidationProvider>
                    </div>
                  </div>
                  <div class="col-md-12 pr-0 pl-0" v-if="psExpanded">
                    <div class="form-group">
                      <label>New Password*</label>
                      <ValidationProvider mode="passive" :rules="!userData.id ? 'required' : ''"  v-slot="{ errors }">
                        <input :type="showPassword ? 'text' : 'password'" 
                          v-model="password" class="form-control" 
                          @input="validatePassword($event.target.value)" />
                        <span class="toggle-password top-50" @click="togglePassword">
                          <i v-if="showPassword" class="fas fa-eye"></i>
                          <i v-else class="fas fa-eye-slash"></i>
                        </span>
                        <password v-model="password" :strength-meter-only="true" style="max-width: unset; margin: 0;" />
                        <span class="text-danger" v-for="(error, index) in errors" :key="index">{{ error }}</span>
                      </ValidationProvider>
                    </div>
                  </div>
                  <div class="col-md-12 pr-0 pl-0" v-if="psExpanded">
                    <ul class="password-checklist">
                      <li :class="{ 'pw-list-item': true, 'pass': checkPwConditionReached('min') == 'pass', 'error': checkPwConditionReached('min') == 'err' }">
                        <span class="pw-list-check"><i :class="`fas ${getPwListIcon('min')}`"></i></span> At least 8 letters
                      </li>
                      <li :class="{ 'pw-list-item': true, 'pass': checkPwConditionReached('uppercase') == 'pass', 'error': checkPwConditionReached('uppercase') == 'err' }">
                        <span class="pw-list-check"><i :class="`fas ${getPwListIcon('uppercase')}`"></i></span> At least 1 capital letter
                      </li>
                      <li :class="{ 'pw-list-item': true, 'pass': checkPwConditionReached('lowercase') == 'pass', 'error': checkPwConditionReached('lowercase') == 'err' }">
                        <span class="pw-list-check"><i :class="`fas ${getPwListIcon('lowercase')}`"></i></span> At least 1 lower case letter
                      </li>
                      <li :class="{ 'pw-list-item': true, 'pass': checkPwConditionReached('symbols') == 'pass', 'error': checkPwConditionReached('symbols') == 'err' }">
                        <span class="pw-list-check"><i :class="`fas ${getPwListIcon('symbols')}`"></i></span> At least 1 special character (!$#@%^*&amp;{}[]&lt;&gt;)
                      </li>
                      <li :class="{ 'pw-list-item': true, 'pass': checkPwConditionReached('digits') == 'pass', 'error': checkPwConditionReached('digits') == 'err' }">
                        <span class="pw-list-check"><i :class="`fas ${getPwListIcon('digits')}`"></i></span> At least 1 numeric digit
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </transition>
          <div class="col-md-12">
            <div class="form-group">
              <label>Roles*</label>
              <ValidationProvider mode="passive" rules="required" v-slot="{ errors }">
                <multiselect
                  v-model="userData.userRoles"
                  :show-labels="false"
                  track-by="id"
                  label="name"
                  :options="allRoles"
                  :multiple="true"
                  :disabled="loggedInUserCompare"
                ></multiselect>
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="col-md-12" v-if="userData.id!=''">
            <div class="form-group">
              <label>Active: <switches 
                :disabled="loggedInUserCompare" 
                v-model="userData.is_enabled" 
                theme="bootstrap" color="primary"></switches></label>
            </div>
          </div>
          <div class="col-md-12" v-if="userData.id==''">
            <div class="form-group">
              <b-form-checkbox v-model="shouldSendWelcomeEmail" >
                Send Welcome Email with password reset link
              </b-form-checkbox>
            </div>
          </div>
          <div class="col-md-12">
            <div class="form-group d-flex justify-content-between">
              <button class="btn btn-primary" type="submit" data-dismiss="modal"
              :disabled="loading">Save</button>
              <button class="btn btn-secondary" type="button" v-if="userData.id != ''"
              :disabled="loading"
                  @click="sendAdminResetPasswordLinkEmail" >Send Reset Password Email</button>
            </div>
          </div>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import passwordValidator from 'password-validator';
import Password from 'vue-password-strength-meter';
import Switches from 'vue-switches';
import { mapActions, mapGetters } from "vuex";
import toast from "../../toast";

export default {
  props: ["userData"],
  data() {
    return {
    	sendResetPasswordEmail:false,
      showUpdateUser: false,
      password: "",
      currentPassword: "",
      errorMessage: "",
      hasError: false,
      loading: false,
      psExpanded: false,
      pwValidator: null,
      pwValidateList: [],
      pwValidateErrorList: [],
      shouldSendWelcomeEmail: false,
      showCurrentPassword: false,
      showPassword: false,
    };
  },
  components: {
    Switches,
    Password
  },
  computed: {
    ...mapGetters(["allRoles"]),
    loggedInUserCompare() {
      return localStorage.getItem('userId') == this.userData.id;
    }
  },
  mounted() {
    if(this.userData.id == '') {
      this.userData.is_enabled = 1;
      this.shouldSendWelcomeEmail = true;
      this.psExpanded = true;
    }
    if (this.allRoles.length == 0) {
      this.$store.dispatch("fetchAllRoles");
    }

    this.initPasswordValidator();
  },
  methods: {
    ...mapActions(["fetchUsers"]),
    focusMyElement() {
      this.$refs.focusThis.focus();
    },
    resetData() {
      let self = this;
      if(self.$router.currentRoute.name == 'globalSearch') {
        let currentPageNumber = localStorage.getItem('currentPageNumber');
        let currentGSTab = localStorage.getItem('currentGSTab');
        self.$store.dispatch('globalSearch', {type: currentGSTab, page: currentPageNumber});
      }
    },
    showConfirmModal() {
      if(this.userData.id == '') {
        this.saveUser();
      } else {
        this.showUpdateUser = true;
      }
    },
    onSaveUser() {
    	this.sendResetPasswordEmail = false;
    	this.saveUser();
    },
    saveUser() {
      let undoMessage;
      if (!this.userData.id) {
        undoMessage = 'User saved.';
        // New user - this.password is required
        this.validatePassword(this.password);
        this.pwValidateErrorList = this.pwValidator.validate(this.password, { list: true });
        if (this.pwValidateErrorList.length > 0) {
          return;
        }
        this.userData.password = this.password;
      } else {
        undoMessage = 'User updated.';
        // Existing user- this.password can be empty
        // if something is typed in password field then validate and send in API request
        if (this.password != "" || this.currentPassword != "") {
          this.validatePassword(this.password);
          this.pwValidateErrorList = this.pwValidator.validate(this.password, { list: true });
          if (this.pwValidateErrorList.length > 0) {
            return;
          }
          if (this.loggedInUserCompare) {
            // the currently logged in user is trying to update their password
            // MUST enter current password to update password
            if (this.currentPassword.trim() === "") {
              this.hasError = true;
              this.errorMessage = "Please enter your current password";
              return;
            } else {
              // if something is typed in currentPassword field, send in API request
              // validate in backend if correct old password
              this.userData.currentPassword = this.currentPassword;
            }
          }
          this.userData.password = this.password;
        }
      }
      if (this.userData.first_name.trim() === "") {
        return;
      }
      this.userData.shouldSendWelcomeEmail = this.shouldSendWelcomeEmail;


      let that = this;
      that.loading = true;
      this.$store.dispatch("updateUser", this.userData).then(function (resp) {
        that.showUpdateUser = false;
        that.loading = false;
        if (resp.data == false) {
          that.hasError = true;
          that.errorMessage = "Email address already in use. Please try a different email address.";
        } else if (resp.data == 'invalid_password') {
          that.hasError = true;
          that.errorMessage = "Invalid new password";
        } else if (resp.data == 'invalid_current_password') {
          that.hasError = true;
          that.errorMessage = "Incorrect current password";
        } else {
          that.hasError = false;
          if (that.sendResetPasswordEmail) {
          	that.loading = true;
          	that.$store
          	  .dispatch("sendAdminResetPasswordLinkEmail", { id:that.userData?.id})
          	  .then((response) => {
          	    that.loading = false;
          	    if (response.data.success) {
          	      that.hasError = false;
          	      toast.success(response.data.msg);
         	 				that.$bvModal.hide("editUserModal");

          	    } else {
          	      that.hasError = true;
          	      that.errorMessage = response.data.msg;
          	    }
          	  })
          	  .catch(err => {
          	    that.loading = false;
          	    that.hasError = true;
          	    that.errorMessage = err;
          	  });
          } else {
          	if (that.$router.currentRoute.name == 'globalSearch') {
          	  let currentPageNumber = localStorage.getItem('currentPageNumber');
          	  let currentGSTab = localStorage.getItem('currentGSTab');
          	  that.$store.dispatch('globalSearch', { type: currentGSTab, page: currentPageNumber });
          	  that.$store.dispatch('manageUndoActivation', undoMessage);
          	}
         	 that.$bvModal.hide("editUserModal");
          }
        }
      });
    },

    // Hardcoded password requirement
    toggleRows() {
      this.psExpanded = !this.psExpanded;
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    toggleCurrentPassword() {
      this.showCurrentPassword = !this.showCurrentPassword;
    },
    initPasswordValidator() {
      if (!this.pwValidator) {
          this.pwValidator = new passwordValidator();

          this.pwValidator.is().min(8).has().uppercase().has().lowercase().has().digits(1).has().symbols();
          this.pwValidateList = this.pwValidator.validate('', { list: true });
      }
    },
    validatePassword(password) {
      this.initPasswordValidator();
      this.pwValidateList = this.pwValidator.validate(password, { list: true });
      this.pwValidateErrorList = this.pwValidateErrorList.filter(x => this.pwValidateList.includes(x));
    },
    checkPwConditionReached(name) {
      if (this.pwValidateErrorList.includes(name)) {
          return 'err';
      }
      if (!this.pwValidateList.includes(name)) {
          return 'pass';
      }

      return 'pending'
    },
    getPwListIcon(name) {
      let code = this.checkPwConditionReached(name);
      if (code == 'err') {
          return 'fa-times';
      }
      if (code == 'pass') {
          return 'fa-check';
      }
      return 'fa-circle';
    },
    sendAdminResetPasswordLinkEmail() {
    	this.sendResetPasswordEmail = true;
      let email = this.userData.email;
      if (email.trim() === "") {
        return;
      }
      this.saveUser(true);
      /*
      let name = this.userData?.first_name + " " + this.userData?.last_name;
      this.loading = true;
      this.$store
        .dispatch("sendAdminResetPasswordLinkEmail", { id:this.userData?.id})
        .then((response) => {
          this.loading = false;
          if (response.data.success) {
            this.hasError = false;
            toast.success(response.data.msg);
          } else {
            this.hasError = true;
            this.errorMessage = response.data.msg;
          }
        })
        .catch(err => {
          this.loading = false;
          this.hasError = true;
          this.errorMessage = err;
        });
        */
    },
  },
  watch: {
    'userData.is_enabled': function (newValue) {
      if (this.userData.id == '') {
        // do not send welcome email on editing
        if (newValue == 1) {
          this.shouldSendWelcomeEmail = true;
        } else if (newValue == true || newValue == false) {
          // If is_enabled becomes false, set shouldSendWelcomeEmail to false and vice-versa
          // do not send welcome email to inactive new user
          this.shouldSendWelcomeEmail = newValue;
        }
      }
    }
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.4s ease-in;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
.toggle-password {
  position: absolute;
  right: 1.5rem;
  transform: translateY(-50%);
  cursor: pointer;
}

.top-50 {
  top: 50%;
}

.top-55 {
  top: 55%;
}

.bordered-link {
  display: block;
  /* border-bottom: 1px solid #ced4da; */
}

.bordered-parent {
  margin: 0px;
  /* border: 1px solid #ced4da; */
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  border-top: none;
  border-radius: 2px;
  /* margin-top: -16px;
  margin-bottom: 1rem; */
}

a:hover {
  text-decoration: none;
}
</style>